class StlQuickViewBase {
  _initVariantSelection() {
    const variantDataEl = this.content.querySelector('.shop-the-look__variant-json');
    if (!variantDataEl) return;

    let variants;
    try {
      variants = JSON.parse(variantDataEl.textContent);
    } catch {
      return;
    }

    const selectedOptions = {};

    this.content.querySelectorAll('.shop-the-look__qv-option').forEach((optionEl) => {
      const position = parseInt(optionEl.dataset.optionPosition, 10);
      const selected = optionEl.querySelector('.is-selected');
      if (selected) selectedOptions[position] = selected.dataset.optionValue;
    });

    this.content.addEventListener('click', (e) => {
      const swatch = e.target.closest('.shop-the-look__qv-swatch, .shop-the-look__qv-size');
      if (!swatch) return;

      const position = parseInt(swatch.dataset.optionPosition, 10);
      const value = swatch.dataset.optionValue;

      selectedOptions[position] = value;

      const optionEl = swatch.closest('.shop-the-look__qv-option');
      optionEl.querySelectorAll('.shop-the-look__qv-swatch, .shop-the-look__qv-size').forEach((btn) => {
        const isSelected = btn.dataset.optionValue === value;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      const selectedValue = optionEl.querySelector('.shop-the-look__qv-selected-value');
      if (selectedValue) selectedValue.textContent = value;

      this._updateVariant(variants, selectedOptions);
    });
  }

  _updateVariant(variants, selectedOptions) {
    const match = variants.find((v) => {
      return v.options.every((optVal, idx) => {
        const pos = idx + 1;
        return !selectedOptions[pos] || selectedOptions[pos] === optVal;
      });
    });

    if (!match) return;

    const idInput = this.content.querySelector('.shop-the-look__variant-id');
    if (idInput) idInput.value = match.id;

    const priceEl = this.content.querySelector('.shop-the-look__qv-price');
    if (priceEl) {
      priceEl.dataset.price = match.price;
      const formatted = this._formatMoney(match.price);
      if (formatted) priceEl.textContent = formatted;
    }

    const atcBtn = this.content.querySelector('.shop-the-look__qv-atc-btn');
    if (atcBtn) {
      const soldOutText = window.shopTheLookStrings?.soldOut || '';
      const addText = window.shopTheLookStrings?.addToCart || '';
      atcBtn.disabled = !match.available;
      const span = atcBtn.querySelector('span');
      if (span) span.textContent = match.available ? addText : soldOutText;
    }
  }

  _formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.shopTheLookStrings?.moneyFormat || '{{amount}}');
    }
    return null;
  }

  _trapFocus(e, panel) {
    if (e.key !== 'Tab') return;
    if (!panel) return;
    const focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) return;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

class ShopTheLookDrawer extends StlQuickViewBase {
  constructor(section) {
    super();
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.drawer = section.querySelector(`#StlDrawer-${this.sectionId}`);
    this.content = section.querySelector(`#StlDrawerContent-${this.sectionId}`);
    this.triggerEl = null;

    if (!this.drawer) return;

    this._bindEvents();
  }

  _bindEvents() {
    this.section.addEventListener('click', (e) => {
      const btn = e.target.closest('.shop-the-look__qv-btn.shop-the-look__desktop-only');
      if (!btn) return;
      if (window.innerWidth < 990) return;
      e.preventDefault();
      this.open(btn);
    });

    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-stl-drawer-close]')) {
        this.close();
      }
    });

    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    this._trapFocusBound = (e) => {
      if (e.key === 'Tab') this._trapFocus(e, this.drawer.querySelector('.shop-the-look__drawer-panel'));
    };
  }

  open(triggerBtn) {
    const productId = triggerBtn.dataset.productId;
    const card = this.section.querySelector(`.shop-the-look__card[data-product-id="${productId}"]`);
    if (!card) return;

    const template = card.querySelector('.shop-the-look__qv-template');
    if (!template) return;

    this.triggerEl = triggerBtn;
    this.content.innerHTML = '';
    this.content.appendChild(template.content.cloneNode(true));

    this._initVariantSelection();

    this.drawer.removeAttribute('hidden');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.drawer.querySelector('.shop-the-look__drawer-panel').focus();
    });

    document.addEventListener('keydown', this._trapFocusBound);
  }

  close() {
    this.drawer.setAttribute('hidden', '');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._trapFocusBound);

    if (this.triggerEl) {
      this.triggerEl.focus();
      this.triggerEl = null;
    }
  }
}

class ShopTheLookMobileSheet extends StlQuickViewBase {
  constructor(section) {
    super();
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.sheet = section.querySelector(`#StlMobileSheet-${this.sectionId}`);
    this.content = section.querySelector(`#StlMobileSheetContent-${this.sectionId}`);
    this.triggerEl = null;

    if (!this.sheet) return;

    this._bindEvents();
  }

  _bindEvents() {
    this.section.addEventListener('click', (e) => {
      const btn = e.target.closest('.shop-the-look__qv-btn.shop-the-look__mobile-only');
      if (!btn) return;
      e.preventDefault();
      this.open(btn);
    });

    this.sheet.addEventListener('click', (e) => {
      if (e.target.closest('[data-stl-sheet-close]')) {
        this.close();
      }
    });

    this.sheet.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    this._trapFocusBound = (e) => {
      if (e.key === 'Tab') this._trapFocus(e, this.sheet.querySelector('.shop-the-look__mobile-sheet-panel'));
    };
  }

  open(triggerBtn) {
    const productId = triggerBtn.dataset.productId;
    const card = this.section.querySelector(`.shop-the-look__card[data-product-id="${productId}"]`);
    if (!card) return;

    const template = card.querySelector('.shop-the-look__mobile-qv-template');
    if (!template) return;

    this.triggerEl = triggerBtn;
    this.content.innerHTML = '';
    this.content.appendChild(template.content.cloneNode(true));

    this._initVariantSelection();

    this.sheet.removeAttribute('hidden');
    this.sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.sheet.querySelector('.shop-the-look__mobile-sheet-panel').focus();
    });

    document.addEventListener('keydown', this._trapFocusBound);
  }

  close() {
    this.sheet.setAttribute('hidden', '');
    this.sheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._trapFocusBound);

    if (this.triggerEl) {
      this.triggerEl.focus();
      this.triggerEl = null;
    }
  }
}

function initStlCarousel(section) {
  const carousel = section.querySelector('.js-swiper-stl-carousel');
  if (!carousel || typeof Swiper === 'undefined') return null;

  return new Swiper(carousel, {
    slidesPerView: 'auto',
    spaceBetween: 20,
    slidesOffsetAfter: 20,
    scrollbar: {
      el: section.querySelector('.js-stl-carousel-scrollbar'),
      draggable: true,
    },
    breakpoints: {
      750: { slidesPerView: 'auto', spaceBetween: 20, slidesOffsetAfter: 0 },
      990: { slidesPerView: 'auto', spaceBetween: 24, slidesOffsetAfter: 0 },
      1100: { slidesPerView: 'auto', spaceBetween: 24, slidesOffsetAfter: 0 },
    },
  });
}

function initStlSection(section) {
  if (section.querySelector('.shop-the-look__drawer')) {
    new ShopTheLookDrawer(section);
  }
  if (section.querySelector('.shop-the-look__mobile-sheet')) {
    new ShopTheLookMobileSheet(section);
  }

  const stlSwiper = initStlCarousel(section);

  const recsEl = section.querySelector('product-recommendations[data-url]');
  if (recsEl && recsEl.querySelector('.js-swiper-stl-carousel')) {
    const observer = new MutationObserver(() => {
      observer.disconnect();
      if (stlSwiper) stlSwiper.destroy(true, true);
      initStlCarousel(section);
    });
    observer.observe(recsEl, { childList: true });
  }
}

document.querySelectorAll('[data-section-id]').forEach(initStlSection);

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('[data-section-id]') || e.target;
  initStlSection(section);
});
